import React, { useEffect, useState, useRef, useContext } from 'react'; // Add useContext
import { useNavigate, Link } from 'react-router-dom'; // Import useNavigate and Link for navigation
import axiosInstance from '../axiosInstance.js';
import TagsInput from './TagsInput'; // Import TagsInput component
import MyUserContext from './../myUserContext.js';

const FollowingDropdown = ({ isOpen, onClose, language, art }) => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [eventTagsMap, setEventTagsMap] = useState({}); // Map of artEventId to eventTags
  const [recommendationsFromMeMap, setRecommendationsFromMeMap] = useState({});
  const [recommendationsToMeMap, setRecommendationsToMeMap] = useState({});
  const dropdownRef = useRef(null);
  const navigate = useNavigate(); // Initialize navigate
  const myUser = useContext(MyUserContext); // Access global myUser context

  useEffect(() => {
    // if (isOpen) {
      axiosInstance
        .get('/getUsers?onlyMyFollowersOrFollowees=true') // Fetch the list of users
        .then((response) => {
          setUsers(response.data); // Directly use response.data
        })
        .catch((error) => {
          console.error('Failed to fetch users:', error);
        });

      // Fetch eventTags for each artEventId
      if (art?.otherPeopleEvents) {
        const fetchEventTags = async () => {
          const tagsMap = {};
          for (const event of art.otherPeopleEvents) {
            try {
              const response = await axiosInstance.get(`/artEvents/${event.artEventId}`);
              tagsMap[event.artEventId] = response.data.event.eventTags || [];
            } catch (error) {
              console.error(`Failed to fetch eventTags for artEventId ${event.artEventId}:`, error);
            }
          }
          setEventTagsMap(tagsMap);
        };
        fetchEventTags();
      }
    // }
  }, [isOpen, art]);

  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!myUser?.myUserInfo?.myUserId) return;

      try {
        // Make requests in parallel
        const [fromResponse, toResponse] = await Promise.all([
          axiosInstance.post('/getRecommendations/filters', {
            fromUserId: myUser.myUserInfo.myUserId,
            artId: art.id
          }),
          axiosInstance.post('/getRecommendations/filters', {
            toUserId: myUser.myUserInfo.myUserId,
            artId: art.id
          })
        ]);

        // Process recommendations from me
        const fromMap = {};
        fromResponse.data.forEach((rec) => {
          fromMap[rec.toUser.id] = rec.recommendation.id;
        });
        setRecommendationsFromMeMap(fromMap);

        // Process recommendations to me
        const toMap = {};
        toResponse.data.forEach((rec) => {
          toMap[rec.fromUser.id] = rec.recommendation.id;
        });
        setRecommendationsToMeMap(toMap);
      } catch (error) {
        console.error('Failed to fetch recommendations:', error);
      }
    };

    if (isOpen) {
      fetchRecommendations();
    }
  }, [isOpen, myUser]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        event.type === "mousedown" // Ensure it's a click, not hover
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const preventParentInteraction = (event) => {
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
        event.preventDefault(); // Prevent parent scrolling only if the event is inside the dropdown
      }
    };

    if (isOpen) {
      document.addEventListener('touchmove', preventParentInteraction, { passive: false });
      document.addEventListener('wheel', preventParentInteraction, { passive: false });
    }

    return () => {
      document.removeEventListener('touchmove', preventParentInteraction);
      document.removeEventListener('wheel', preventParentInteraction);
    };
  }, [isOpen]);

  useEffect(() => {
    const preventParentScroll = (event) => {
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
        event.stopPropagation(); // Prevent scroll events from propagating to the parent
      }
    };

    if (isOpen) {
      dropdownRef.current?.addEventListener('wheel', preventParentScroll, { passive: false });
      dropdownRef.current?.addEventListener('touchmove', preventParentScroll, { passive: false });
    }

    return () => {
      dropdownRef.current?.removeEventListener('wheel', preventParentScroll);
      dropdownRef.current?.removeEventListener('touchmove', preventParentScroll);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'; // Disable scrolling on the body
    } else {
      document.body.style.overflow = ''; // Re-enable scrolling on the body
    }

    return () => {
      document.body.style.overflow = ''; // Ensure scrolling is re-enabled on cleanup
    };
  }, [isOpen]);

  const toggleUserSelection = (userId) => {
    if (hasEvent(userId)) {
      // If the new user has an event without a "todo" tag, unselect all other users
      if (selectedUsers.length == 1 && selectedUsers[0] === userId) {
        // If the user is already selected, unselect them
        setSelectedUsers([]);
      } else {
        // Unselect all other users
        setSelectedUsers([userId]);
      }
    } else { // userId can be recommended to
      setSelectedUsers((prevSelected) => {
        const userWithEvent = prevSelected.find(
          (userId) =>
            hasEvent(userId) ||
            hasRecommendationFromMe(userId) ||
            hasRecommendationToMe(userId)
        );
        if (userWithEvent) { // there was someone who can't be recommended to
          return [userId];// prevSelected.filter((id) => id !== userWithEventWithoutTodo && id !== userId);
        }
        // Add userId to selectedUsers if not already selected
        return prevSelected.includes(userId)
          ? prevSelected.filter((id) => id !== userId)
          : [...prevSelected, userId];
      });
    }
  };

  const handleRecommend = (toUserIDs) => {
    const updatedMap = { ...recommendationsFromMeMap };

    Promise.all(
      toUserIDs.map((userId) =>
        axiosInstance
          .post('/postRecommendation', { toUserId: userId, artId: art.id })
          .then((response) => {
            updatedMap[userId] = response.data.recommendation.id; // Update map with new recommendation
          })
          .catch((error) => {
            console.error(`Failed to recommend to user ${userId}:`, error);
          })
      )
    ).then(() => {
      setRecommendationsFromMeMap(updatedMap); // Update state after all requests are completed
    });
  };

  const handleClose = () => {
    onClose(); // Close the dropdown when the emoji is clicked
  };

  const hasEvent = (userId, countTodoAsEvent = true) => {
    const eventId = art?.otherPeopleEvents?.find(e => e.userId === userId)?.artEventId;
    const tags = eventTagsMap[eventId]?.map(tag => tag.name) || [];
    if (countTodoAsEvent) {
      return eventId; // Consider "todo" events as valid events
    } else {
      return eventId && !tags.includes("todo");
    }
  };
  const hasRecommendationFromMe = (userId) => {
    return recommendationsFromMeMap[userId] !== undefined;
  };
  const hasRecommendationToMe = (userId) => {
    return recommendationsToMeMap[userId] !== undefined;
  };

  // const hasEventWithTodoTagOrNoEvent = (userId) => {
  //   const eventId = art?.otherPeopleEvents?.find(e => e.userId === userId)?.artEventId;
  //   const tags = eventTagsMap[eventId]?.map(tag => tag.name) || [];
  //   return !eventId || tags.includes("todo");
  // };

  const navigateToDiscussion = (userId) => {
    const eventId = art?.otherPeopleEvents?.find(e => e.userId === userId)?.artEventId;
    // if (eventId) {
      navigate(`/artEvents/${eventId}`); // Navigate to the corresponding art event
    // }
  };

  if (!isOpen) return null;

  console.log('recommendationsFromMeMap', recommendationsFromMeMap);
  console.log('recommendationsToMeMap', recommendationsToMeMap);

  return (
    <div
      ref={dropdownRef}
      className="pb-20 md:pb-0 fixed inset-x-0 bottom-0 bg-[#181818] border-t border-gray-500 shadow-lg max-h-[75vh] overflow-y-auto z-50 w-full md:w-1/4 md:mx-auto sm:max-h-[50vh] rounded-lg"
    >
      <div className="sticky top-0 flex justify-center text-center items-center py-2 space-x-2 font-bold text-white bg-[#5D6064]">
        {language === "ru" ? "Взаимодействие с друзьями" : "Interraction with friends"}
      </div>
      <div className="grid grid-cols-3 gap-4 p-4">
        {users.map((user) => (
          <div
            key={user.id}
            className="text-center cursor-pointer"
            onClick={() => toggleUserSelection(user.id)}
          >
            <div
              className={`w-16 h-16 rounded-full mx-auto border-4 ${
                selectedUsers.includes(user.id) ? "border-blue-500" : "border-transparent"
              }`}
            >
              <img
                src={user.avatarUrl}
                alt={user.userName}
                className="w-full h-full rounded-full"
              />
            </div>
            <p className="text-sm mt-2">
              {user.userName.length > 10 ? `${user.userName.slice(0, 10)}...` : user.userName}
            </p>
            {/* Display TagsInput below the user name */}
            <div className="flex justify-center items-center mt-1">
              <TagsInput
                initialTags={
                  eventTagsMap[art?.otherPeopleEvents?.find(e => e.userId === user.id)?.artEventId]
                    ?.map(tag => tag.name) || []
                }
                editable={false}
                language={language}
                reducedView={true}
              />
            </div>
            {/* Display recommendation status as plain text */}
            {recommendationsFromMeMap[user.id] && (
              <p className="text-blue-500 text-xs mt-1">
                {language === "ru" ? "Вы рекомендовали" : "You recommended"}
              </p>
            )}
            {recommendationsToMeMap[user.id] && (
              <p className="text-green-500 text-xs mt-1">
                {language === "ru" ? "Вам рекомендовали" : "Recommended to you"}
              </p>
            )}
          </div>
        ))}
      </div>
      {/* Fixed Recommend button */}
      <div className="sticky bottom-0 bg-[#5D6064] z-10">
        {selectedUsers.length === 1 && (
          <>
            {hasEvent(selectedUsers[0]) && (
              <button
                className="bg-green-500 text-white px-4 py-2 rounded w-full mb-2"
                onClick={() => navigateToDiscussion(selectedUsers[0])} // Navigate to discussion
              >
                {language === "ru" ? "Перейти к обсуждению в библиотеке пользователя" : "Go to discussion in user's library"}
              </button>
            )}
            {hasRecommendationFromMe(selectedUsers[0]) && (
              <button
                className="bg-blue-500 text-white px-4 py-2 rounded w-full mb-2"
                onClick={() => navigate(`/recommendations/${recommendationsFromMeMap[selectedUsers[0]]}`)} // Navigate to recommendation from me
              >
                {language === "ru" ? "Перейти к моей рекомендации" : "Go to my recommendation"}
              </button>
            )}
            {hasRecommendationToMe(selectedUsers[0]) && (
              <button
                className="bg-blue-500 text-white px-4 py-2 rounded w-full mb-2"
                onClick={() => navigate(`/recommendations/${recommendationsToMeMap[selectedUsers[0]]}`)} // Navigate to recommendation to me
              >
                {language === "ru" ? "Перейти к рекомендации от пользователя" : "Go to recommendation from user"}
              </button>
            )}
            {!hasEvent(selectedUsers[0], false) &&
              !hasRecommendationFromMe(selectedUsers[0]) &&
              !hasRecommendationToMe(selectedUsers[0]) && (
                <button
                  className="bg-blue-500 text-white px-4 py-2 rounded w-full"
                  onClick={()=>{
                    handleRecommend([selectedUsers[0]]); 
                  }}
                >
                  {language === "ru" ? "Рекомендовать" : "Recommend"}
                </button>
              )}
          </>
        )}
        {selectedUsers.length > 1 &&
          selectedUsers.every(
            (userId) =>
              !hasEvent(userId, false) &&
              !hasRecommendationFromMe(userId) &&
              !hasRecommendationToMe(userId)
          ) && (
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded w-full"
              onClick={()=>{
                handleRecommend(selectedUsers);
              }} // Placeholder for recommendation logic
            >
              {language === "ru" ? "Рекомендовать отдельно" : "Recommend separately"}
            </button>
          )}
      </div>
    </div>
  );
};

export default FollowingDropdown;
